/** Document geometry and guides (Image and View menus, Geometry and Guide dialogs). */
import { contentLabel, type Rect } from '@maipo/agent-protocol';
import {
  AddGuideCommand,
  ClearGuidesCommand,
  CropDocumentCommand,
  FlipDocumentCommand,
  ResizeCanvasCommand,
  ResizeImageCommand,
  RotateDocumentCommand,
  type CanvasAnchor,
  type DocumentRotation,
} from '../../core/commands';
import { GuideLayoutCommand, type GuideLayoutSettings } from '../../core/guide-layout';
import { walkLayers, type EditorDocument } from '../../core/model';
import type { Diagnostic, ValidateContext } from '../registry';
import { error } from '../intents/common';
import { defineCommand, documentFootprint, DOCUMENT_RESOURCES, resourceFootprint } from './registry';

/**
 * Document geometry moves every layer. For agents (lock policy `enforce`) a
 * locked or position-locked layer anywhere therefore refuses the whole
 * operation: locks are the user's "do not touch" (ADR-009 §5.2). The user
 * keeps native semantics, where geometry has never checked locks.
 */
function checkGeometryLocks(document: EditorDocument, context: ValidateContext): Diagnostic[] {
  if (context.policy.locks !== 'enforce') return [];
  const locked = walkLayers(document.root).find((layer) => layer.id !== document.root.id && (layer.locked || layer.positionLocked));
  return locked ? [error('LAYER_LOCKED', `Layer “${contentLabel(locked.name, 80)}” is locked; document geometry would move it. The user can unlock it first.`, locked.id)] : [];
}

const geometry = { footprint: () => documentFootprint() };

export const resizeImage = defineCommand<{ width: number; height: number; resolution?: number }>({
  id: 'document.resizeImage',
  group: 'document',
  backs: ['action.resize-image', 'dialog.geometry.image'],
  label: () => 'Resize image',
  ...geometry,
  validate: (document, _params, context) => checkGeometryLocks(document, context),
  compile: (_document, params) => [new ResizeImageCommand(params.width, params.height, params.resolution)],
});

export const resizeCanvas = defineCommand<{ width: number; height: number; anchor?: CanvasAnchor }>({
  id: 'document.resizeCanvas',
  group: 'document',
  backs: ['action.resize-canvas', 'dialog.geometry.canvas'],
  label: () => 'Resize canvas',
  ...geometry,
  validate: (document, _params, context) => checkGeometryLocks(document, context),
  compile: (_document, params) => [new ResizeCanvasCommand(params.width, params.height, params.anchor ?? 'center')],
});

export const crop = defineCommand<{ rect: Rect }>({
  id: 'document.crop',
  group: 'document',
  backs: ['action.crop-selection'],
  label: () => 'Crop document',
  ...geometry,
  validate: (document, params, context) => {
    const diagnostics = checkGeometryLocks(document, context);
    const { x, y, width, height } = params.rect;
    if (x >= document.width || y >= document.height || x + width <= 0 || y + height <= 0) diagnostics.push(error('COMMAND_REJECTED', 'Crop area does not intersect the canvas.'));
    return diagnostics;
  },
  compile: (_document, params) => [new CropDocumentCommand(params.rect)],
});

export const rotate = defineCommand<{ direction: DocumentRotation }>({
  id: 'document.rotate',
  group: 'document',
  backs: ['action.rotate-cw', 'action.rotate-ccw', 'action.rotate-180'],
  label: (params) => new RotateDocumentCommand(params.direction).label,
  ...geometry,
  validate: (document, _params, context) => checkGeometryLocks(document, context),
  compile: (_document, params) => [new RotateDocumentCommand(params.direction)],
});

export const flip = defineCommand<{ axis: 'horizontal' | 'vertical' }>({
  id: 'document.flip',
  group: 'document',
  backs: ['action.flip-document-horizontal', 'action.flip-document-vertical'],
  label: (params) => new FlipDocumentCommand(params.axis).label,
  ...geometry,
  validate: (document, _params, context) => checkGeometryLocks(document, context),
  compile: (_document, params) => [new FlipDocumentCommand(params.axis)],
});

const guides = resourceFootprint(DOCUMENT_RESOURCES.guides);

export const addGuide = defineCommand<{ axis: 'vertical' | 'horizontal'; position: number }>({
  id: 'guides.add',
  group: 'guides',
  backs: ['action.add-guide', 'dialog.guide'],
  label: () => 'Add guide',
  footprint: () => guides,
  validate: () => [],
  compile: (_document, params) => [new AddGuideCommand(params.axis, params.position)],
});

export const clearGuides = defineCommand<Record<string, never>>({
  id: 'guides.clear',
  group: 'guides',
  backs: ['action.clear-guides'],
  label: () => 'Clear guides',
  footprint: () => guides,
  validate: () => [],
  compile: () => [new ClearGuidesCommand()],
});

export const guideLayout = defineCommand<Partial<GuideLayoutSettings> & Pick<GuideLayoutSettings, 'columns' | 'rows'>>({
  id: 'guides.layout',
  group: 'guides',
  backs: ['action.new-guide-layout', 'dialog.guide-layout'],
  label: () => 'New guide layout',
  footprint: () => guides,
  validate: () => [],
  compile: (_document, params) => [new GuideLayoutCommand({
    columns: params.columns,
    rows: params.rows,
    columnGutter: params.columnGutter ?? 0,
    rowGutter: params.rowGutter ?? 0,
    marginTop: params.marginTop ?? 0,
    marginRight: params.marginRight ?? 0,
    marginBottom: params.marginBottom ?? 0,
    marginLeft: params.marginLeft ?? 0,
    replaceExisting: params.replaceExisting ?? true,
  })],
});

export const DOCUMENT_COMMANDS = [resizeImage, resizeCanvas, crop, rotate, flip, addGuide, clearGuides, guideLayout] as const;
